import React, {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
} from "react";

import { datadogRum } from "@datadog/browser-rum";
import {
  type QueryObserverResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import posthog from "posthog-js";
import { useMatch, useNavigate } from "react-router";
import { useLocation } from "react-router-dom";

import backendClient from "src/api";
import {
  type ClusterRead,
  type EntityRead,
  type Me,
  type MembershipWithEntities,
  type Token,
} from "src/client";
import { storeRedirectPath } from "src/experiences/auth/hooks/redirect";
import authService from "src/services/authService";

const INVITATION_ROUTES = ["/verify-email", "/create-password", "/login"];

const AUTH_ROUTES = [
  "/mfa-verify",
  "/magic-link-confirmation-sent",
  "/password-setup-confirmation-sent",
  "/password-reset-confirmation-sent",
  "/reset-password",
  "/auth/azure-sso-callback",
  "/auth/oidc/callback",
  "/auth/sso/initiate",
  ...INVITATION_ROUTES,
];

const isAuthRoute = (pathname: string) => {
  return AUTH_ROUTES.some((route) => pathname.startsWith(route));
};

const isInvitationRoute = (pathname: string): boolean => {
  return INVITATION_ROUTES.some((route) => pathname.startsWith(route));
};

export interface UserContextProps {
  user: Me;
  membership: MembershipWithEntities;
  memberships: MembershipWithEntities[];
  cluster: ClusterRead;
  entity: EntityRead;
  isUserLoading: boolean;
  refreshUser: () => Promise<QueryObserverResult<any, unknown>>;
  logUserIn: (session: Token) => Promise<QueryObserverResult<any, unknown>>;
  logout: () => void;
  isStaff?: boolean;
  isSuperStaff?: boolean;
}

// @ts-expect-error
const initialState: UserContextProps = {};

const UserContext = createContext(initialState);

interface UserProviderProps {
  children: ReactNode;
}

function UserProvider({ children }: UserProviderProps): React.ReactElement {
  const session = authService.getSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const hasSession = useMemo(() => Boolean(session), [session]);

  const {
    data: user,
    refetch: refreshUser,
    isLoading: isUserLoading,
  } = useQuery({
    enabled: hasSession,
    queryKey: ["user"],
    queryFn: () => backendClient.default.getMe(),
  });

  useEffect(() => {
    if (!user) return;
    if (
      ["production", "staging", "demo"].includes(
        import.meta.env.VITE_ENVIRONMENT
      )
    ) {
      // If user is being impersonated, use impersonator's details for analytics
      const analyticsId = user.impersonatorId || user.id;
      const analyticsEmail = user.impersonatorEmail || user.email;
      const analyticsName = user.impersonatorFullName || user.fullName;

      // Set Datadog user context with impersonation info
      datadogRum.setUser({
        id: analyticsId,
        name: analyticsName,
        email: analyticsEmail,
      });

      // Set PostHog user context with impersonation info
      posthog.identify(analyticsId, {
        email: analyticsEmail,
        name: analyticsName,
      });
    }
  }, [user]);

  useEffect(() => {
    // If there is a user session and the current route is an invitation route, refresh the user
    if (hasSession && isInvitationRoute(location.pathname)) {
      refreshUser();
    } else if (!hasSession && !isAuthRoute(location.pathname)) {
      const fullPath = `${location.pathname}${location.search}${location.hash}`;
      storeRedirectPath(fullPath);
    }
  }, [location, hasSession]);

  const clusterMatch = useMatch({
    path: "/clusters/:clusterId/*",
  });
  const clusterId = clusterMatch?.params?.clusterId;

  const entityMatch = useMatch({
    path: "/entities/:entityId/*",
  });
  const entityId = entityMatch?.params?.entityId;

  let cluster;
  let entity;
  let membership;
  const memberships = user?.memberships;

  if (clusterId) {
    membership = user?.memberships.find(
      ({ cluster }) => cluster?.id === clusterId
    );
    cluster = membership?.cluster;
  } else if (entityId) {
    membership = user?.memberships.find(
      ({ entity }) => entity?.id === entityId
    );
    entity = membership?.entity;
  } else {
    membership = user?.memberships[0];
    if (membership?.cluster) {
      cluster = membership.cluster;
    } else if (membership?.entity) {
      entity = membership.entity;
    }
  }

  const logUserIn = async (session: Token): Promise<UseQueryResult> => {
    authService.handleAuthentication(session);
    const response = await refreshUser();

    if (response.status === "error") navigate("/");

    return response;
  };

  const logout = (): void => {
    authService.logout();
    queryClient.removeQueries({
      queryKey: ["user"],
    });
    navigate("/");
  };

  const isStaff = user?.isStaff;
  const isSuperStaff = user?.isSuperStaff;

  return (
    <UserContext.Provider
      value={{
        // @ts-expect-error
        user,
        // @ts-expect-error
        memberships,
        // @ts-expect-error
        membership,
        // @ts-expect-error
        cluster,
        // @ts-expect-error
        entity,
        refreshUser,
        isUserLoading: isUserLoading && hasSession,
        logUserIn,
        logout,
        isStaff,
        isSuperStaff,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export { UserProvider, UserContext };
